import axios, { AxiosError } from 'axios'
import merge from 'lodash/merge'
import {
  BankrateFees,
  BankrateGatewayDimensions,
  BankrateLocation,
  BankrateOffer,
  BankrateProduct,
  BankrateProductFamilies,
  MortgageRateTableDimensions,
} from '@bankrate/mortgage-types'
import orderBy from 'lodash/orderBy'
import nthMerge from 'utils/nthMerge'
import querystring from 'query-string'
import partition from 'utils/partition'
import { RecursivePartial } from 'types/utils'
import { SortBy, SortRules } from 'types/offers'
import {
  BankrateDisplayOffer,
  getNullOffer,
  isEditorial,
  isSponsored,
  sageSituationalSponsoredOffer,
} from 'utils/offers'
import { action, Action, AnyFunction, computed, Computed, thunk, Thunk } from 'easy-peasy'
import { rollbar } from 'utils/rollbar'
import { StoreModel } from 'store'
import { updateQueryParams } from '../../utils/queryParams'
import { stateToQueryParamsMap } from './dimensions'
import debounce from 'lodash/debounce'

type DisplayTarget =
  | 'desktopRateTable'
  | 'mobileRateTable'
  | 'featuredRateTable'
  | 'desktopSecondaryRT'
  | 'mobileSecondaryRT'
  | 'featuredSecondaryRT'
  | 'desktopPaidRT'
  | 'mobilePaidRT'
  | 'featuredPaidRT'

type LocationTypes = 'rate-table' | 'additional offers' | 'featured tab' | 'rate-table-stacked' | 'rate-table-all'

export type CtaModal = { advertiserId: number; epLink: string; jwtLink: string } | null

export interface OffersModel {
  // State
  sortBy: SortBy
  isLoading: boolean
  defaultSortRules: SortRules
  pristineExtraOffers: RecursivePartial<BankrateOffer>[]
  pristineFeaturedOffers: RecursivePartial<BankrateOffer>[]
  pristineOffers: RecursivePartial<BankrateOffer>[]
  pagination: { limit: number; current: number }
  noCoverageReason: string
  addShowStackedOffersParam: boolean
  ctaModal: CtaModal

  // Computed
  extraOffers: Computed<OffersModel, BankrateOffer[], StoreModel>
  hasPaidOffers: Computed<OffersModel, boolean>
  normalizedBankrateOffers: Computed<OffersModel, BankrateDisplayOffer[]>
  normalizedExtraOffers: Computed<OffersModel, BankrateDisplayOffer[]>
  offers: Computed<OffersModel, BankrateDisplayOffer[], StoreModel>
  showingStacked: Computed<OffersModel, boolean, StoreModel>
  stackedCriterias: Computed<OffersModel, Record<'flag' | 'sort' | 'enoughOffers', boolean>, StoreModel>
  paidOffersCount: Computed<OffersModel, number>
  uniquePaidAdvertisersCount: Computed<OffersModel, number>
  sortRules: Computed<OffersModel, SortRules, StoreModel>
  canGetOffers: Computed<OffersModel, boolean, StoreModel>

  // Actions
  showMoreOffers: Action<OffersModel>
  setSortBy: Action<OffersModel, SortBy>
  setIsLoading: Action<OffersModel, boolean>
  setOffers: Action<OffersModel, BankrateOffer[]>
  setFeaturedOffers: Action<OffersModel, BankrateOffer[]>
  setExtraOffers: Action<OffersModel, BankrateOffer[]>
  setSortRules: Action<OffersModel, RecursivePartial<SortRules>>
  setNoCoverageReason: Action<OffersModel, string>
  resetPagination: Action<OffersModel>
  setStackedQueryParameter: Action<OffersModel, MortgageRateTableDimensions>
  setAddShowStackedOffersParam: Action<OffersModel, boolean>
  setCtaModal: Action<OffersModel, CtaModal>

  // Thunks
  getOffers: Thunk<OffersModel, MortgageRateTableDimensions, unknown, StoreModel>
}

const GATEWAY_FEATURE_FLAGS = ['enableLcl']
const DEFAULT_SORT = 'none'

const offers: OffersModel = {
  // State
  isLoading: true,
  addShowStackedOffersParam: true,
  sortBy: DEFAULT_SORT,
  pagination: { limit: 12, current: 1 },
  pristineFeaturedOffers: [] as BankrateOffer[],
  pristineExtraOffers: [] as BankrateOffer[],
  pristineOffers: [] as BankrateOffer[],
  defaultSortRules: {
    none: { isPaid: 'desc', hasRequiredFees: 'desc' },
    rate: { isPaid: 'desc', rate: 'asc', apr: 'asc' },
    apr: { isPaid: 'desc', apr: 'asc', rate: 'asc' },
    fees: { isPaid: 'desc', upFrontCosts: 'asc', rate: 'asc' },
    payment: { isPaid: 'desc', estimatedPayment: 'asc', rate: 'asc' },
    relevance: { isPaid: 'desc', hasRequiredFees: 'desc', eightYearCost: 'asc', apr: 'asc', points: 'asc' },
    noEightYear: { isPaid: 'desc', hasRequiredFees: 'desc', apr: 'asc', points: 'asc' },
  },
  noCoverageReason: '',
  ctaModal: null,

  // Computed
  sortRules: computed([state => state, (state, storeState) => storeState.featureFlags.flags], (state, flags) => {
    return state.defaultSortRules
  }),

  extraOffers: computed(
    [
      state => state,
      (state, storeState) => storeState.dimensions.activeDisplayTab,
      (state, storeState) => storeState.dimensions.displayTargets,
      (state, storeState) => storeState.featureFlags.flags,
    ],
    (state, activeDisplayTab, displayTargets, flags) => {
      const filteredOffers = filterOffersByDisplay(
        state.normalizedExtraOffers,
        flags['showStackedOffers'] ? 'stacked' : activeDisplayTab,
        displayTargets
      )
      // extra offers need to be sorted separately b/c they display below primary search offers
      const sortCriteria = state.sortRules[state.sortBy]
      return orderBy(filteredOffers, Object.keys(sortCriteria), Object.values(sortCriteria))
    }
  ),

  hasPaidOffers: computed(state => {
    return state.paidOffersCount + state.extraOffers.length > 0
  }),

  // the mortgage gateway has a fun quirk where it has different contracts for the offers it returns
  // so they do not have the BankrateOffer type interface that the offer service has
  normalizedBankrateOffers: computed(state => normalizedOffers(state.pristineOffers as BankrateOffer[], 'primary')),
  normalizedExtraOffers: computed(state => normalizedOffers(state.pristineExtraOffers as BankrateOffer[], 'extra')),

  offers: computed(
    [
      state => state,
      // @ts-ignore
      (state, storeState) => storeState.location.location,
      (state, storeState) => storeState.dimensions,
      (state, storeState) => storeState.featureFlags.flags,
    ],
    (state, location, dimensions, flags) => {
      /** Filters offers by display group (all, featured) */
      const featuredOffers = filterOffersByDisplay(
        state.normalizedBankrateOffers,
        'featured',
        dimensions.displayTargets
      )
      const filteredOffers = filterOffersByDisplay(
        state.showingStacked
          ? overrideOfferLocation(state.normalizedBankrateOffers, 'rate-table-all')
          : state.normalizedBankrateOffers,
        dimensions.activeDisplayTab,
        dimensions.displayTargets
      )

      if (
        !dimensions.dimensionsRecentlyChanged &&
        meetsSituationalSponsoredOffersCriteria(
          filteredOffers,
          dimensions.dimensions,
          location,
          flags.enableSageSponsored as boolean
        )
      ) {
        return [sageSituationalSponsoredOffer]
      }

      /** Sorts offers based on current sort rule */
      let sortCriteria = state.sortRules[state.sortBy]

      // If all offers are missing an 8 year cost then we want to sort them differently
      const no8YrCost = state.normalizedBankrateOffers.every(offer => !offer.eightYearCost)
      if (state.sortBy === DEFAULT_SORT && no8YrCost) {
        sortCriteria = state.sortRules['noEightYear']
      }

      const sortedOffers = orderBy(filteredOffers, Object.keys(sortCriteria), Object.values(sortCriteria))

      /** Stack feature offers at the top if offering stacked experience */
      if (state.showingStacked) {
        sortedOffers.unshift(
          ...orderBy(
            overrideOfferLocation(featuredOffers, 'rate-table-stacked'),
            Object.keys(sortCriteria),
            Object.values(sortCriteria)
          )
        )
      }

      /** Inserts sponsored offers every 4th offer */
      let arrangedOffers
      if (dimensions.activeDisplayTab === 'all') {
        arrangedOffers = arrangeOffersAll(sortedOffers, state.extraOffers, state.paidOffersCount, location, flags)
      } else {
        arrangedOffers = arrangeOffersFeatured(sortedOffers, state.extraOffers, state.paidOffersCount, location, flags)
      }

      /** Paginates through offers based on the amount of offers and current page */
      const { current, limit } = state.pagination
      let paginationLimit = current * limit

      // this logic has been deprecated, but can be re-enabled for future use
      // if (state.showingStacked) {
      //   paginationLimit = featuredOffers.length + (current - 1) * limit
      // }

      return arrangedOffers.slice(0, paginationLimit)
    }
  ),

  showingStacked: computed([state => state], state => {
    // all criterias are true -> enable stacked
    return Object.values(state.stackedCriterias).every(value => value)
  }),

  stackedCriterias: computed(
    [
      state => state,
      (_, storeState) => storeState.featureFlags.flags,
      (_, storeState) => storeState.dimensions.dimensions.mortgageType,
    ],
    (state, flags, mortgageType) => {
      const featuredMin =
        typeof globalThis.stackedOfferMinLength === 'object' &&
        typeof globalThis.stackedOfferMinLength[mortgageType] === 'number'
          ? globalThis.stackedOfferMinLength
          : { Purchase: 8, Refinance: 4 }
      return {
        flag: Boolean(flags['showStackedOffers']),
        sort: state.sortBy === DEFAULT_SORT,
        enoughOffers: state.pristineFeaturedOffers.length >= featuredMin[mortgageType],
      }
    }
  ),

  // used for ep links ul/pl aka we charge based on whats being offered
  paidOffersCount: computed(state => {
    if (state.normalizedBankrateOffers.length === 0) {
      return 0
    }
    return state.normalizedBankrateOffers.filter(offer => offer.isPaid).length
  }),

  uniquePaidAdvertisersCount: computed(state => {
    if (state.normalizedBankrateOffers.length === 0) {
      return 0
    }
    return new Set(state.normalizedBankrateOffers.filter(offer => offer.isPaid).map(offer => offer.advertiser.id)).size
  }),

  canGetOffers: computed(
    [
      // @ts-ignore
      (state, storeState) => storeState.dimensions,
      (state, storeState) => storeState.initialization,
    ],
    (dimensions, initialization) => {
      return (
        !dimensions.hasInvalidDimension &&
        initialization.hasInitialized &&
        !dimensions.dimensionsValidation.zipCode.invalid
      )
    }
  ),

  // Actions
  setSortBy: action((state, sortBy) => {
    state.sortBy = sortBy
  }),

  setOffers: action((state, payload) => {
    state.pristineOffers = payload
  }),

  setFeaturedOffers: action((state, payload) => {
    state.pristineFeaturedOffers = payload
  }),

  setExtraOffers: action((state, payload) => {
    state.pristineExtraOffers = payload
  }),

  setIsLoading: action((state, payload) => {
    state.isLoading = payload
  }),

  setSortRules: action((state, payload) => {
    state.defaultSortRules = merge(state.defaultSortRules, payload)
  }),

  showMoreOffers: action(state => {
    state.pagination.current += 1
  }),

  setNoCoverageReason: action((state, payload) => {
    state.noCoverageReason = payload
  }),

  resetPagination: action(state => {
    state.pagination.current = 1
  }),

  setStackedQueryParameter: action((state, payload) => {
    if (state.addShowStackedOffersParam) {
      const newParams = { ...payload, showingStacked: state.showingStacked || 'all' }
      updateQueryParams(newParams, stateToQueryParamsMap)
    }
  }),

  setAddShowStackedOffersParam: action((state, payload) => {
    state.addShowStackedOffersParam = payload
  }),

  setCtaModal: action((state, payload: CtaModal) => {
    state.ctaModal = payload
  }),

  // Thunks
  getOffers: thunk(
    async (actions, dimensions: MortgageRateTableDimensions, { getStoreActions, getState, getStoreState }) => {
      // prevent making a call to the gateway when there are invalid gateway query params
      if (!getStoreState()['offers'].canGetOffers) {
        actions.setIsLoading(false)
        return
      }
      actions.setIsLoading(true)

      // for a new search, update tracking correlation ids
      getStoreActions()['tracking'].setNewCorrelationIds()

      const { increment } = getStoreActions()['datadog']
      // datadog metric to see if this is in sync with how many requests the mrt auth user sends
      increment({ name: 'searches', value: 1 })

      const mrtFeatureFlags = getStoreState().featureFlags.flags
      const gatewayFeatureFlags = GATEWAY_FEATURE_FLAGS.filter(flag => mrtFeatureFlags[flag] === true)
      const deviceTypes = getStoreState()['dimensions'].deviceType
      const locationState = getStoreState()['location'].location.state

      const gatewayQueryParams = mapDimensionsToV4ApiParams(
        dimensions,
        deviceTypes,
        locationState,
        gatewayFeatureFlags,
        mrtFeatureFlags
      )

      const { offers, metadata } = await fetch(gatewayQueryParams, mrtFeatureFlags, increment)

      if (metadata?.reasonForNoCoverage) {
        actions.setNoCoverageReason(metadata.reasonForNoCoverage)
      }

      actions.setOffers(
        filterOffersByAdvertiserLimit(offers, getStoreState().featureFlags.flags.advertiserOfferLimit as number)
      )
      actions.setFeaturedOffers(filterOffersByDisplay(offers, 'featured', getStoreState().dimensions.displayTargets))

      const needsExtraOffers = offers.length <= 3 && gatewayQueryParams.loanTerms.includes('30yr')
      if (needsExtraOffers || mrtFeatureFlags['forceExtraOffers']) {
        const response = await fetch(
          {
            ...gatewayQueryParams,
            loanTerms: ['20yr', '15yr', '5-1arm'],
          },
          mrtFeatureFlags,
          increment
        )

        const extraOffers: BankrateOffer[] = response.offers.filter((offer: BankrateOffer) => offer.type === 'rate') // paid only

        // same datadog metric to see if this is in sync with how many requests the mrt auth user sends
        increment({ name: 'searches', value: 1 })
        increment({ name: 'additionalProducts.search', value: 1 })

        actions.setExtraOffers(extraOffers)
      } else {
        // reset after each search
        actions.setExtraOffers([])
      }

      debouncedSetLoading(actions.setIsLoading, false)
      getStoreActions().dimensions.setDimensionsRecentlyChanged(false)

      if (mrtFeatureFlags['showStackedOffers']) {
        actions.setStackedQueryParameter(dimensions)
        actions.setAddShowStackedOffersParam(false)
      }
    }
  ),
}

const debouncedSetLoading = debounce((setIsLoadinAct: any, value: boolean) => {
  setIsLoadinAct(value)
}, 300)

const checkError = (
  err: AxiosError
): {
  reportTitle?: string
  tag: string
} => {
  if (err.response === undefined) {
    return { tag: 'error_type:connectivity' }
  }

  if (err.response.status === 404) {
    return { tag: 'error_type:invalid_zip' }
  }

  if (err.response.status === 401) {
    return { tag: 'error_type:unauthorized' }
  }

  const { message, validation } = err.response.data as { message: string; validation: { [key: string]: string[] } }
  if (message && message === 'product not found for given dimensions') {
    return { tag: 'error_type:no_product' }
  }

  if (validation) {
    const fields = validation.keys.join('-')

    return { reportTitle: message, tag: `error_type:validation,fields:${fields}` }
  }

  const backendName = err.request.clientId ? 'api' : 'gateway'
  return {
    reportTitle: `mortgage ${backendName} service responded with ${err.response.status}`,
    tag: 'error_type:unknown',
  }
}

const arrangeOffersAll = (
  offers: BankrateOffer[],
  extraOffers: BankrateOffer[],
  paidCount: number,
  location: BankrateLocation,
  flags: {
    [key: string]: any
  }
) => {
  const [sponsored, nonSponsored] = partition<BankrateOffer>(offers, isSponsored)
  const [editorial, regular] = partition<BankrateOffer>(nonSponsored, isEditorial)

  let paidOffers = regular
  if (sponsored.length) {
    paidOffers = nthMerge(
      regular,
      sponsored, // Sponsored offers are already randomized by the mortgage-api
      3
    )
    // add help desk after sponsored offer
    if (flags['showHelpDesk']) {
      paidOffers.splice(4, 0, getNullOffer('help-desk'))
    }
  } else {
    // add help desk in 4th position and there is more than 2 offers
    if (paidCount >= 3 && flags['showHelpDesk']) {
      paidOffers.splice(3, 0, getNullOffer('help-desk'))
    }
  }

  if (paidCount && paidCount < 3 && flags['showHelpDesk']) {
    paidOffers.push(getNullOffer('help-desk'))
  }

  // add low coverage extra offers after paid and before editorial
  if (extraOffers.length) {
    paidOffers.push(getNullOffer('low-coverage-extra-offers'))
    paidOffers = paidOffers.concat(extraOffers)
  }

  // if offers <= 3, show branded row and rateForIt row
  if (paidCount) {
    if (paidCount + extraOffers.length <= 3) {
      paidOffers.push(getNullOffer('branded'))
      paidOffers.push(getNullOffer('rate-for-it'))
    }
  }

  if (editorial && editorial.length > 0) {
    editorial.unshift(getNullOffer('editorial-disclosure'))
  }

  // add editorial offers to the end
  return paidOffers.concat(editorial)
}

const arrangeOffersFeatured = (
  offers: BankrateOffer[],
  extraOffers: BankrateOffer[],
  paidCount: number,
  location: BankrateLocation,
  flags: {
    [key: string]: any
  }
) => {
  const [sponsored, nonSponsored] = partition<BankrateOffer>(offers, isSponsored)
  const [editorial, regular] = partition<BankrateOffer>(nonSponsored, isEditorial)

  // distribute a sponsored offer every n paid offers
  let paidOffers = nthMerge(
    regular,
    sponsored, // Sponsored offers are already randomized by the mortgage-api
    3
  )
  // add help desk after paid and sponsored offers
  if (flags['showHelpDesk']) {
    paidOffers.push(getNullOffer('help-desk'))
  }

  // add low coverage extra offers after paid and before editorial
  if (extraOffers.length) {
    paidOffers.push(getNullOffer('low-coverage-extra-offers'))
    paidOffers = paidOffers.concat(extraOffers)
  }

  // when lowCoverage, show branded row and rateForIt row
  if (paidCount && paidCount + extraOffers.length <= 3) {
    paidOffers.push(getNullOffer('branded'))
    paidOffers.push(getNullOffer('rate-for-it'))
  }

  if (editorial && editorial.length > 0) {
    editorial.unshift(getNullOffer('editorial-disclosure'))
  }

  // add editorial offers to the end
  return paidOffers.concat(editorial)
}

export const filterOffersByDisplay = (offers: BankrateOffer[], display: string, displayTargets: DisplayTarget[]) => {
  switch (display) {
    case 'all':
    default:
      return offers.filter(offer =>
        displayTargets
          .filter(
            desiredTarget =>
              desiredTarget !== 'featuredRateTable' &&
              desiredTarget !== 'featuredSecondaryRT' &&
              desiredTarget !== 'featuredPaidRT'
          )
          .some(desiredTarget => offer.displayTargets?.includes(desiredTarget))
      )

    case 'featured':
      // need to map over the set of all offers, identifying those instances
      // where an offer's advertiser.id matches at least one featured offer,
      // and then filter that result set according to the rules governing featured offers

      // create a set of featured advertisers to serve as a reference map
      const featuredOffersAdvertisers = offers.reduce((accum: number[], offer) => {
        if (offer.isFeatured) accum.push(offer.advertiser.id)
        return accum
      }, [])

      // we want only featured offers with specific display targets
      const featuredOffersWithUnfilteredProducts = offers
        .filter(offer => featuredOffersAdvertisers.includes(offer.advertiser.id))
        .filter(
          offer =>
            offer.displayTargets &&
            (offer.displayTargets.includes('featuredRateTable') ||
              offer.displayTargets.includes('featuredSecondaryRT') ||
              offer.displayTargets.includes('featuredPaidRT'))
        )

      // ensure offers contain only the lowest eightYearCost in the set
      // of offers with the same product name, if more than one exists;
      // essentially, replace duplicate product named offers
      // with those offers having a lower eightYearCost
      return Object.values(
        featuredOffersWithUnfilteredProducts.reduce(
          (
            accum: {
              [key: string]: any
            },
            offer
          ) => {
            const offerKey = `${offer.advertiser.id}_${offer.product.name}`
            if (!accum[offerKey] || (offer.eightYearCost || 0) < accum[offerKey].eightYearCost) accum[offerKey] = offer
            return accum
          },
          {}
        )
      ) as BankrateOffer[]
  }
}

const overrideOfferLocation = (offers: BankrateDisplayOffer[], location: LocationTypes) => {
  const flaggedOffers: BankrateDisplayOffer[] = []

  offers.forEach((offer: BankrateDisplayOffer) => {
    flaggedOffers.push({ ...offer, locationOverride: location })
  })

  return flaggedOffers
}

export const filterOffersByAdvertiserLimit = (
  offers: BankrateOffer[],
  advertiserOfferLimit: number = -1
): BankrateOffer[] => {
  if (advertiserOfferLimit <= 0) {
    return offers
  }

  let filteredOffers: BankrateOffer[] = []
  let advertiserProductCounts: {
    [key: string]: any
  } = {}

  offers.forEach(offer => {
    const countKey = `${offer.advertiser.name}_${offer.product?.name}`
    advertiserProductCounts[countKey] = advertiserProductCounts[countKey] + 1 || 1

    if (advertiserProductCounts[countKey] <= advertiserOfferLimit) {
      filteredOffers.push(offer)
    }
  })

  return filteredOffers
}

function meetsSituationalSponsoredOffersCriteria(
  offers: BankrateOffer[],
  dimensions: MortgageRateTableDimensions,
  location: BankrateLocation,
  enableSageSponsored: boolean
) {
  const loanAmount =
    dimensions.mortgageType === 'Purchase'
      ? dimensions.purchase.price - dimensions.purchase.downPayment
      : dimensions.refinance.loanAmount

  const loanTerms =
    dimensions.mortgageType === 'Purchase' ? dimensions.purchase.loanTerms : dimensions.refinance.loanTerms

  return (
    enableSageSponsored &&
    !offers.some(o => o.isPaid) &&
    dimensions.mortgageType === 'Refinance' &&
    dimensions.refinance.cashOutAmount &&
    dimensions.user.creditScore >= 640 &&
    loanAmount >= 150000 &&
    loanTerms.some(lt => lt.includes('yr')) &&
    !dimensions.user.fha &&
    [
      'CA',
      'CO',
      'FL',
      'GA',
      'IL',
      'MD',
      'NC',
      'NJ',
      'TX',
      'IN',
      'MI',
      'MN',
      'OH',
      'OR',
      'PA',
      'SC',
      'TN',
      'AL',
      'IA',
      'ID',
      'KS',
      'NH',
      'OK',
      'WI',
    ].includes(location.state)
  )
}

export function mapDimensionsToV4ApiParams(
  queryParams: MortgageRateTableDimensions,
  deviceTypes: string,
  locationState: string,
  gatewayFeatureFlags: string[],
  mrtFeatureFlags: { [key: string]: unknown }
): BankrateGatewayDimensions {
  const e2eTestEnabled = querystring.parse(window.location.search).e2eTestEnabled === 'true'

  const isPurchase = queryParams.mortgageType === 'Purchase'
  const isDesktop = deviceTypes === 'desktop'
  let params = {
    loanType: isPurchase ? 'purchase' : 'refinance',
    propertyValue: isPurchase ? queryParams.purchase.price : queryParams.refinance.propertyValue,
    propertyType: isPurchase ? queryParams.purchase.propertyType : queryParams.refinance.propertyType,
    propertyUse: isPurchase ? queryParams.purchase.propertyUse : queryParams.refinance.propertyUse,
    cashOutAmount: isPurchase ? 0 : queryParams.refinance.cashOutAmount,
    zipCode: queryParams.zipCode,
    loanAmount: isPurchase
      ? queryParams.purchase.price - queryParams.purchase.downPayment
      : queryParams.refinance.loanAmount,
    creditScore: Number(queryParams.user.creditScore),
    debtToIncomeRatio: queryParams.user.debtToIncomeRatio,
    pointsRange: isPurchase ? queryParams.purchase.points : queryParams.refinance.points,
    productFamilies: getProductFamilies(queryParams),
    loanTerms: isPurchase ? queryParams.purchase.loanTerms : queryParams.refinance.loanTerms,
    defaultSearch: !queryParams.searchChanged,
    pid: queryParams.partnerId,
    veteranStatus: queryParams.user.veteranStatus,
    hadPriorVaLoan: queryParams.user.hadPriorVaLoan,
    hasVaDisabilities: queryParams.user.hasVaDisabilities,
    firstTimeHomeBuyer: queryParams.user.firstTimeHomebuyer,
    displayTargets: isDesktop ? globalThis.displayTargetsByDevice.desktop : globalThis.displayTargetsByDevice.mobile,
    deviceTypes: deviceTypes.split(','),
    targetRuleId: queryParams.targetRuleId,
  }

  const featureFlags: string[] = updateFlags(gatewayFeatureFlags, locationState, mrtFeatureFlags, queryParams)

  params = Object.assign(params, { e2eTestEnabled }, { featureFlags })
  // @ts-ignore
  return params
}

function getProductFamilies(queryParams: MortgageRateTableDimensions): BankrateProductFamilies[] {
  const families: BankrateProductFamilies[] = ['conventional']
  if (queryParams.user.veteranStatus !== 'NoMilitaryService') {
    families.push('va')
  }
  if (queryParams.user.fha) {
    families.push('fha')
  }
  return families
}

function updateFlags(
  gatewayFeatureFlags: string[],
  locationState: string,
  mrtFeatureFlags: { [key: string]: unknown },
  dimensions: MortgageRateTableDimensions
): string[] {
  const loanType = dimensions.mortgageType
  const loanTerm = loanType === 'Purchase' ? dimensions.purchase.loanTerms : dimensions.refinance.loanTerms
  const bidderStates = mrtFeatureFlags['rateBidderStates'] as string[]

  let updatedGatewayFeatureFlags = [...gatewayFeatureFlags]

  if (
    loanType.toLowerCase() === 'refinance' &&
    loanTerm.filter(term => term === '30yr').length > 0 &&
    bidderStates.filter(state => state === locationState).length > 0
  ) {
    updatedGatewayFeatureFlags.push('optimizedPricing')
  }

  if (mrtFeatureFlags['testMode']) {
    updatedGatewayFeatureFlags.push('testMode')
  }

  return updatedGatewayFeatureFlags
}

export function normalizedOffers(
  offers: BankrateOffer[],
  rowType: 'primary' | 'extra' | 'situational'
): BankrateDisplayOffer[] {
  const defaultProduct = offers.find((offer: BankrateOffer) => offer.type === 'rate')?.product || {}

  return offers.map((offer: BankrateOffer) => {
    return {
      ...(offer as BankrateOffer),
      id: offer.id || (offer as any).offerId,
      isPaid: offer.type === 'editorial' ? false : true,
      tracking: {
        // @ts-ignore sponsored offers are cpc, rate are tracking.link and editorial do not have links
        link: offer.type === 'sponsored' ? offer.cpc : offer?.tracking?.link || '',
      },
      // sponsored offers have phone in different spot
      advertiser: {
        ...offer.advertiser,
        // @ts-ignore
        phone: offer.type === 'sponsored' ? offer.phoneNumber : offer.advertiser.phone,
      },
      // sponsored offers don't have a default product
      product: (offer.type === 'sponsored' ? defaultProduct : offer.product) as BankrateProduct,
      // https://redventures.atlassian.net/browse/MX-1217 this can be === 3 when this ticket is done
      hasRequiredFees:
        offer.type !== 'sponsored' ? offer.fees.filter((fee: BankrateFees) => fee.required === true).length >= 3 : true,
      // differentiate primary, extra, and none offers
      rowType,
    } as unknown as BankrateDisplayOffer
  })
}

export async function fetch(
  params: any,
  flags: {
    [key: string]: unknown
  },
  increment: AnyFunction
) {
  let apiEndpoint: string

  if (flags.useQaGateway) {
    apiEndpoint = 'https://mortgage-api.qa.bankrate.com'
  } else if (flags.useProdGateway) {
    apiEndpoint = 'https://mortgage-api.bankrate.com'
  } else {
    apiEndpoint = process.env.REACT_APP_MORTGAGE_API_URL!
  }
  apiEndpoint += '/rates/v4/'

  //valid mortgage api clientId is required
  params = {
    ...params,
    clientId: 'MortgageRateTable',
    includeSponsored: true,
    includeEditorial: true,
  }
  try {
    const response = await axios.get(apiEndpoint, { params })

    return { offers: response.data.offers, metadata: response.data.meta }
  } catch (err: any) {
    const { reportTitle, tag } = checkError(err)

    if (reportTitle) {
      rollbar.error(reportTitle, apiEndpoint, params)
    }

    increment({ name: 'request.error', value: 1, tags: [...tag, { tag: 'backend:mortgage_api' }] })
    return { offers: [], metadata: { reasonForNoCoverage: 'error' } }
  }
}

export default offers
